'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import {
  QR_OVERLAY_STYLES,
  type QrOverlayStatus
} from '@/lib/qr/qr-overlay-styles';
import type { QrBboxData } from '@/lib/qr/decode';

interface QrOverlayProps {
  /** Status drives the colour palette and badge text. When null the overlay
   *  renders nothing — callers pass null for pages whose QR status isn't
   *  meaningful (e.g. before a scan completes). */
  status: QrOverlayStatus | null;
  /** Server-persisted corner data. Wins when present — it's deterministic and
   *  doesn't require a client-side decode of the served (often compressed)
   *  image. */
  qrBbox?: QrBboxData | null;
  /** Client-decoded corner fallback. Used only when `qrBbox` is null — typical
   *  for legacy rows uploaded before persistence existed. */
  liveCorners?: { x: number; y: number }[] | null;
  /** Optional label appended to the corner chip — e.g. the decoded barcode. */
  label?: string | null;
}

/** Renders the status-coloured polygon + corner chip on top of a page image.
 *  The parent must position this inside a wrapper that fills the un-rotated
 *  image bounds; the overlay's SVG and chip are absolute-positioned at
 *  0..1 fractions of that wrapper so they scale and rotate with it. */
export function QrOverlay({
  status,
  qrBbox,
  liveCorners,
  label
}: QrOverlayProps) {
  const t = useTranslations('qrOverlay');
  if (!status) return null;
  const styles = QR_OVERLAY_STYLES[status];

  const corners =
    qrBbox?.corners?.length === 4
      ? qrBbox.corners
      : liveCorners?.length === 4
        ? liveCorners
        : null;

  // Axis-aligned bbox used to position the chip directly above the QR.
  const bbox = corners
    ? (() => {
        const xs = corners.map((c) => c.x);
        const ys = corners.map((c) => c.y);
        return {
          left: Math.min(...xs),
          top: Math.min(...ys)
        };
      })()
    : null;

  return (
    <>
      {corners && (
        <svg
          className='pointer-events-none absolute inset-0 h-full w-full'
          viewBox='0 0 1 1'
          preserveAspectRatio='none'
          aria-hidden
        >
          <polygon
            points={corners.map((c) => `${c.x},${c.y}`).join(' ')}
            className={cn(
              styles.fill,
              styles.stroke,
              'drop-shadow-[0_0_3px_rgba(0,0,0,0.35)]'
            )}
            style={{
              strokeWidth: 2,
              vectorEffect: 'non-scaling-stroke'
            }}
          />
        </svg>
      )}
      <div
        className='pointer-events-none absolute z-10'
        style={
          bbox
            ? {
                left: `${bbox.left * 100}%`,
                top: `${bbox.top * 100}%`,
                transform: 'translateY(-100%)',
                paddingBottom: 4
              }
            : { top: 8, insetInlineEnd: 8 }
        }
      >
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap shadow-sm',
            styles.badge
          )}
        >
          {t(styles.badgeText)}
          {label ? (
            <span className='font-mono text-[10px] opacity-90'>· {label}</span>
          ) : null}
        </span>
      </div>
    </>
  );
}
